// npm
import React from 'react'
import PropTypes from 'prop-types'

// material-ui
import withStyles from '@material-ui/core/styles/withStyles'

// utils
import {keyListener, KEYS} from 'utils/key-listener'

// constants
import {DEFAULT_STROKE, DEFAULT_FONT_SIZE, LABEL_FONT_FAMILY, LABEL_LINE_HEIGHT} from './constants'

const MIN_WIDTH = 60;
const CARET_ALLOWANCE = 4;

// measured with a canvas 2D context, so the editor grows to exactly the width Konva will render
// the label at (a `ch`-based estimate badly undersizes wide capitals like "W"/"M")
let measuringContext = null;
const measureLineWidth = (line, fontSize) => {
	if (!measuringContext) {
		measuringContext = document.createElement('canvas').getContext('2d');
	}
	measuringContext.font = `${fontSize}px ${LABEL_FONT_FAMILY}`;
	return measuringContext.measureText(line).width;
};

// left/right arrows are global question-navigation shortcuts - suspend them while typing, the
// same as every other text input in the player (see workings.jsx)
const disableArrowKeyListeners = () => {
	keyListener.disable(KEYS.LEFT_ARROW);
	keyListener.disable(KEYS.RIGHT_ARROW);
};

const enableArrowKeyListeners = () => {
	keyListener.enable(KEYS.LEFT_ARROW);
	keyListener.enable(KEYS.RIGHT_ARROW);
};


// LabelEditor (not connected to styles)
// --------------------------------------------------------------------------

// an in-place textarea laid over the Konva stage at the label's position, styled to match the
// rendered Konva Text (same font, size, line height and transform) so committing it doesn't
// visibly jump. Enter commits, Shift+Enter adds a line, Escape cancels, clicking away commits.
class LabelEditor extends React.Component {
	constructor(props) {
		super(props);
		this.state = {value: props.label.text || ''};
		this.textareaRef = React.createRef();
		this.finished = false;

		this.handleChange = this.handleChange.bind(this);
		this.handleKeyDown = this.handleKeyDown.bind(this);
		this.commit = this.commit.bind(this);
	}

	componentDidMount() {
		// focused synchronously (not in a timeout) so this still counts as part of the user's
		// click/tap - mobile browsers only raise the on-screen keyboard inside a user gesture
		const textarea = this.textareaRef.current;
		if (textarea) {
			textarea.focus();
			textarea.setSelectionRange(textarea.value.length, textarea.value.length);
		}
	}

	componentWillUnmount() {
		// unmounting while focused doesn't fire onBlur, so don't leave navigation disabled
		enableArrowKeyListeners();
	}

	handleChange(e) {
		this.setState({value: e.target.value});
	}

	handleKeyDown(e) {
		if (e.key === 'Escape') {
			e.preventDefault();
			this.finish(() => this.props.onCancel());
		} else if (e.key === 'Enter' && !e.shiftKey) {
			e.preventDefault();
			this.commit();
		}
	}

	// public - the canvas also calls this when the stage is clicked mid-edit
	commit() {
		this.finish(() => this.props.onCommit(this.state.value));
	}

	// Enter/Escape unmount the editor, which can also fire a blur - only act on the first
	finish(callback) {
		if (this.finished) {
			return;
		}
		this.finished = true;
		callback();
	}

	render() {
		const {classes, label} = this.props;
		const {value} = this.state;
		const lines = value.split('\n');
		const fontSize = label.fontSize || DEFAULT_FONT_SIZE;
		const widestLine = Math.max(...lines.map((line) => measureLineWidth(line, fontSize)));

		const style = {
			left: label.x,
			top: label.y,
			width: Math.max(MIN_WIDTH, Math.ceil(widestLine) + CARET_ALLOWANCE),
			height: lines.length * fontSize * LABEL_LINE_HEIGHT,
			fontSize,
			color: label.stroke || DEFAULT_STROKE,
			transform: `rotate(${label.rotation || 0}deg) scale(${label.scaleX || 1}, ${label.scaleY || 1})`
		};

		return (
			<textarea
				ref={this.textareaRef}
				className={classes.textarea}
				style={style}
				value={value}
				placeholder="Label"
				onChange={this.handleChange}
				onKeyDown={this.handleKeyDown}
				onFocus={disableArrowKeyListeners}
				onBlur={this.commit}
				rows={1}
				wrap="off"
				spellCheck={false}
			/>
		);
	}
}

LabelEditor.propTypes = {
	label: PropTypes.shape({
		x: PropTypes.number.isRequired,
		y: PropTypes.number.isRequired,
		text: PropTypes.string
	}).isRequired,
	onCommit: PropTypes.func.isRequired,
	onCancel: PropTypes.func.isRequired
};


// LabelEditor (connected to styles)
// --------------------------------------------------------------------------

const styles = ({palette}) => ({
	textarea: {
		position: 'absolute',
		zIndex: 1,
		transformOrigin: '0 0',
		boxSizing: 'content-box',
		margin: 0,
		padding: 0,
		border: 'none',
		outline: `1px dashed ${palette.primary.main}`,
		outlineOffset: 2,
		backgroundColor: 'rgba(255, 255, 255, 0.9)',
		fontFamily: LABEL_FONT_FAMILY,
		lineHeight: LABEL_LINE_HEIGHT,
		whiteSpace: 'pre',
		overflow: 'hidden',
		resize: 'none',
		cursor: 'text'
	}
});

// (MUI 3's withStyles forwards `innerRef` to this inner instance, which the canvas uses to call commit())
LabelEditor = withStyles(styles)(LabelEditor);


// Export
// --------------------------------------------------------------------------
export {LabelEditor}
